import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import DoneAllIcon from "@mui/icons-material/DoneAll";

export const FinalizarInterManualAdmin = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [ticajes, setTicajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [ordenes, setOrdenes] = useState([]);

  const [loading, setLoading] = useState(true);

  // =========================
  // Modal finalizar (Dialog)
  // =========================
  const [modalFinalizar, setModalFinalizar] = useState(false);
  const [ticajeSeleccionado, setTicajeSeleccionado] = useState(null);

  const [finManual, setFinManual] = useState("");
  const [motivoManual, setMotivoManual] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

      const [resT, resU, resC, resCentros, resOts] = await Promise.all([
        fetch(`${Global.url}ticajes/proceso`, { headers }),
        fetch(`${Global.url}usuarios`, { headers }),
        fetch(`${Global.url}clientes`, { headers }),
        fetch(`${Global.url}centros/todos`, { headers }),
        fetch(`${Global.url}ordenes`, { headers }),
      ]);

      if (!resT.ok) toast.error("No se pudieron obtener las intervenciones en proceso.");
      if (!resU.ok) toast.warning("No se pudieron cargar los usuarios (se mostrarán IDs).");
      if (!resC.ok) toast.warning("No se pudieron cargar clientes.");
      if (!resCentros.ok) toast.warning("No se pudieron cargar centros.");
      if (!resOts.ok) toast.warning("No se pudieron cargar OT.");

      const [dataT, dataU, dataC, dataCent, dataOts] = await Promise.all([
        resT.ok ? resT.json() : [],
        resU.ok ? resU.json() : [],
        resC.ok ? resC.json() : [],
        resCentros.ok ? resCentros.json() : [],
        resOts.ok ? resOts.json() : [],
      ]);

      setTicajes(Array.isArray(dataT) ? dataT : []);
      setUsuarios(Array.isArray(dataU) ? dataU : []);
      setClientes(Array.isArray(dataC) ? dataC : []);
      setCentros(Array.isArray(dataCent) ? dataCent : []);
      setOrdenes(Array.isArray(dataOts) ? dataOts : []);
    } catch (error) {
      console.error("Error cargando datos:", error);
      toast.error("Error de conexión con el servidor.");
      setTicajes([]);
      setUsuarios([]);
      setClientes([]);
      setCentros([]);
      setOrdenes([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Helpers
  // =========================
  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    if (!dateValue) return "";
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  // =========================
  // Lookups (ids -> label)
  // =========================
  const mapUsuario = useMemo(() => {
    return Object.fromEntries(
      (usuarios || []).map((u) => [
        String(u.id),
        [u.nombre, u.apellidos].filter(Boolean).join(" ").trim() || u.nombre || `ID ${u.id}`,
      ])
    );
  }, [usuarios]);

  const mapCliente = useMemo(() => {
    return Object.fromEntries((clientes || []).map((c) => [String(c.id), c.nombre || `Cliente ${c.id}`]));
  }, [clientes]);

  const mapCentro = useMemo(() => {
    return Object.fromEntries((centros || []).map((c) => [String(c.id), c.nombre || `Centro ${c.id}`]));
  }, [centros]);

  const mapOrden = useMemo(() => {
    return Object.fromEntries(
      (ordenes || []).map((o) => [String(o.id), o.titulo || o.nombre || `OT ${o.id}`])
    );
  }, [ordenes]);

  // =========================
  // Apertura/cierre modal finalizar
  // =========================
  const abrirFinalizar = (t) => {
    setTicajeSeleccionado(t);
    setFinManual(toDatetimeLocalString(new Date()));
    setMotivoManual("");
    setFormError("");
    setModalFinalizar(true);
  };

  const cerrarFinalizar = () => {
    if (saving) return;
    setModalFinalizar(false);
    setTicajeSeleccionado(null);
    setFinManual("");
    setMotivoManual("");
    setFormError("");
  };

  // =========================
  // Finalizar intervención (POST)
  // =========================
  const finalizarIntervencion = async () => {
    if (!ticajeSeleccionado) return;

    if (!finManual || !motivoManual.trim()) {
      setFormError("Debes indicar la fecha/hora de fin y un motivo.");
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}ticajes/finalizar-manualmente`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          id: ticajeSeleccionado.id,
          fin: finManual, // el backend hace new Date(fin)
          observaciones_admin: motivoManual.trim(),
        }),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        toast.error(result?.message || "Error al finalizar la intervención.");
        return;
      }

      toast.success("Intervención finalizada correctamente.");
      cerrarFinalizar();
      await cargarDatos();
    } catch (error) {
      console.error("Error al finalizar:", error);
      toast.error("Error de conexión al finalizar la intervención.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // DataGrid: rows + columns
  // =========================
  const rows = useMemo(
    () =>
      (ticajes || []).map((t) => ({
        ...t,
        usuario_nombre: mapUsuario[String(t.usuario_id)] || t.usuario || `ID ${t.usuario_id ?? "-"}`,
        cliente_nombre: mapCliente[String(t.cliente_id)] || t.cliente || `ID ${t.cliente_id ?? "-"}`,
        centro_nombre: mapCentro[String(t.centro_id)] || t.centro || `ID ${t.centro_id ?? "-"}`,
        orden_nombre: mapOrden[String(t.orden_id)] || t.orden || (t.orden_id ? `OT ${t.orden_id}` : "-"),
        inicio_txt: fmtDate(t.inicio),
        comentarios_txt: t.comentarios?.trim() ? t.comentarios : "Sin comentarios",
      })),
    [ticajes, mapUsuario, mapCliente, mapCentro, mapOrden]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "cliente_nombre", headerName: "Cliente", flex: 1, minWidth: 220 },
      { field: "centro_nombre", headerName: "Centro", flex: 1, minWidth: 220 },
      { field: "orden_nombre", headerName: "OT", flex: 1, minWidth: 240 },
      { field: "inicio_txt", headerName: "Inicio", minWidth: 180 },
      { field: "comentarios_txt", headerName: "Comentarios", flex: 1, minWidth: 260 },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const t = params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
              <Button size="small" startIcon={<DoneAllIcon />} onClick={() => abrirFinalizar(t)}>
                Finalizar
              </Button>
            </Box>
          );
        },
      },
    ],
    []
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Finalizar Intervenciones en Proceso
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay intervenciones activas actualmente."}
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        {/* =========================
            Modal: Finalizar intervención
           ========================= */}
        <Dialog open={modalFinalizar} onClose={cerrarFinalizar} fullWidth maxWidth="sm">
          <DialogTitle>Finalizar intervención</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {ticajeSeleccionado
                  ? `${mapUsuario[String(ticajeSeleccionado.usuario_id)] || ticajeSeleccionado.usuario || ""} · ${
                      mapCentro[String(ticajeSeleccionado.centro_id)] || ticajeSeleccionado.centro || ""
                    } · Inicio: ${fmtDate(ticajeSeleccionado.inicio)}`
                  : ""}
              </Typography>

              <TextField
                label="Fecha/hora fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={finManual}
                onChange={(e) => setFinManual(e.target.value)}
              />

              <TextField
                label="Motivo / observaciones"
                value={motivoManual}
                onChange={(e) => setMotivoManual(e.target.value)}
                placeholder="Motivo del cierre manual"
                multiline
                minRows={3}
              />

              {formError && <Typography color="error">{formError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarFinalizar} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={finalizarIntervencion} disabled={saving}>
              {saving ? "Finalizando..." : "Finalizar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
